// src/components/Admin/ChatManager/Chat/UserMessageStats.jsx
import { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Button,
    Stack,
    CircularProgress,
    Divider,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { koKR } from '@mui/x-date-pickers/locales';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import api from '../../../../api/axios';

dayjs.locale('ko');

export default function UserMessageStats() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(false);

    const [startDate, setStartDate] = useState(dayjs().startOf('month'));
    const [endDate, setEndDate] = useState(dayjs());

    /* ───── 데이터 로드 ────────────────────────────────── */
    const loadStats = useCallback(() => {
        setLoading(true);
        api
            .get('/api/chat/logs/user-stats/', {
                params: {
                    start_date: startDate.format('YYYY-MM-DD'),
                    end_date: endDate.format('YYYY-MM-DD'),
                },
            })
            .then((res) => setStats(res.data.results || []))
            .catch(() => alert('데이터를 불러오지 못했습니다.'))
            .finally(() => setLoading(false));
    }, [startDate, endDate]);

    useEffect(loadStats, [loadStats]);

    /* ───── 엑셀 내보내기 ───────────────────────────────── */
    const exportToExcel = () => {
        const rows = stats.map((s) => [s.username, s.message_count]);
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet([['아이디(닉네임)', '채팅 건수'], ...rows]);
        XLSX.utils.book_append_sheet(wb, ws, 'UserMessageStats');
        const blob = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(
            new Blob([blob], { type: 'application/octet-stream' }),
            `user_message_stats_${Date.now()}.xlsx`,
        );
    };

    // ✅ 모바일은 "하얀 Paper 박스" 제거: Box로 대체 (ChatLogPage랑 동일 패턴)
    const FilterWrap = isMobile ? Box : Paper;
    const filterWrapProps = isMobile ? {} : { variant: 'outlined' };

    return (
        <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="ko"
            localeText={koKR.components.MuiLocalizationProvider.defaultProps.localeText}
        >
            <Box
                sx={{
                    p: isMobile ? 1.5 : 3,
                    width: '100%',
                    minWidth: 0,
                    overflowX: 'hidden',
                }}
            >
                {/* 필터 바 */}
                <FilterWrap
                    {...filterWrapProps}
                    sx={{
                        p: isMobile ? 0 : 2,
                        mb: 3,
                        borderRadius: 2,
                        width: '100%',
                        minWidth: 0,
                        ...(isMobile ? { bgcolor: 'transparent', border: 0, boxShadow: 'none' } : {}),
                    }}
                >
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        flexWrap="nowrap"
                        sx={{ minWidth: 0, width: '100%' }}
                    >
                        {/* 날짜 선택 */}
                        {isMobile ? (
                            // ✅ 모바일: 세로 + fullWidth (가로 스크롤 X)
                            <Stack spacing={1.5} sx={{ width: '100%', minWidth: 0 }}>
                                <DatePicker
                                    label="시작일"
                                    value={startDate}
                                    onChange={(v) => setStartDate(v)}
                                    format="YYYY-MM-DD"
                                    slotProps={{
                                        textField: { size: 'small', fullWidth: true, sx: { minWidth: 0 } },
                                    }}
                                />
                                <DatePicker
                                    label="종료일"
                                    value={endDate}
                                    onChange={(v) => setEndDate(v)}
                                    format="YYYY-MM-DD"
                                    slotProps={{
                                        textField: { size: 'small', fullWidth: true, sx: { minWidth: 0 } },
                                    }}
                                />
                            </Stack>
                        ) : (
                            // ✅ PC: 가로
                            <Stack direction="row" spacing={2} sx={{ minWidth: 0 }}>
                                <DatePicker
                                    label="시작일"
                                    value={startDate}
                                    onChange={(v) => setStartDate(v)}
                                    format="YYYY-MM-DD"
                                    slotProps={{
                                        textField: { size: 'small', sx: { minWidth: 140 } },
                                    }}
                                />
                                <DatePicker
                                    label="종료일"
                                    value={endDate}
                                    onChange={(v) => setEndDate(v)}
                                    format="YYYY-MM-DD"
                                    slotProps={{
                                        textField: { size: 'small', sx: { minWidth: 140 } },
                                    }}
                                />
                            </Stack>
                        )}

                        <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ display: { xs: 'none', md: 'block' } }}
                        />

                        {/* 버튼 묶음 */}
                        <Stack
                            direction={isMobile ? 'column' : 'row'}
                            spacing={2}
                            sx={{ ml: { md: 'auto' }, width: isMobile ? '100%' : 'auto' }}
                        >
                            <Button
                                variant="contained"
                                onClick={loadStats}
                                fullWidth={isMobile}
                                sx={isMobile ? { height: 40 } : undefined}
                            >
                                조회
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={exportToExcel}
                                fullWidth={isMobile}
                                sx={isMobile ? { height: 40 } : undefined}
                            >
                                엑셀로 저장
                            </Button>
                        </Stack>
                    </Stack>
                </FilterWrap>

                {/* 데이터 테이블 */}
                <Paper sx={{ p: 2, width: '100%', minWidth: 0 }} variant="outlined">
                    {loading ? (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Table size="small" sx={{ width: '100%' }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center">아이디(닉네임)</TableCell>
                                    <TableCell align="center">채팅 건수</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {stats.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={2} align="center">
                                            데이터가 없습니다
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    stats.map((s) => (
                                        <TableRow key={s.username}>
                                            <TableCell align="center">{s.username}</TableCell>
                                            <TableCell align="center">{s.message_count}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    )}
                </Paper>
            </Box>
        </LocalizationProvider>
    );
}
